import { Stack } from '@mantine/core'
import { OverviewHeader } from '../components/overview/OverviewHeader'
import { CriticalAttention } from '../components/overview/attention'
import { LiveOperations, SystemHealthPanel, RecentExecutions } from '../components/overview/operations'
import { ThroughputPanels, TopEntitiesChart, WorkerUtilization } from '../components/overview/charts'
import { SignalInboxPreview } from '../components/overview/previews'
import { GovernanceQueuePreview } from '../components/governance/GovernanceQueuePreview'
import { ReportRegistryPreview } from '../components/registry/ReportRegistryPreview'

/**
 * The Observatory Overview.
 *
 * Reading order answers the three questions from the brief, in order: is
 * Lighthouse healthy (header KPIs), what needs attention (Critical Attention),
 * what is happening right now (Live Operations + System Health) — and only then
 * the deeper detail.
 *
 * NO PANEL IS A FIXTURE ANY MORE. Every figure here is served: the API's own
 * health checks, the worker and source projections, live operations, ETL halts,
 * the Data Comb's latest pass, hourly throughput, successful-load volumes,
 * recent entity runs, the signal store behind the Issues and Critical tiles and
 * the Signal Inbox preview, and — for Lighthouse.Admin holders — the governance
 * queue and report registry. The signal figures are only as good as the store's
 * coverage says (a dash when the detector never ran or has stopped, a green 0
 * only under Complete coverage). What nothing measures says so in words rather
 * than drawing a plausible number: the Scheduler, Notification Service and File
 * Storage rows nothing health-checks.
 *
 * This page SUMMARISES; it does not duplicate the tools beneath it. Every panel
 * with a destination ends in a link to the surface that owns its subject.
 *
 * Every widget owns its own loading, empty and error state. There is deliberately
 * no page-level guard, so one failing subsystem degrades one panel instead of
 * blanking the whole operations surface.
 *
 * The two admin previews gate THEMSELVES on the server's answer on
 * Lighthouse.Admin (hooks/useLighthouseAdmin.ts): a caller without the role sees
 * two compact restricted cards, and neither card polls an endpoint that would
 * answer 403.
 */
export function DashboardPage() {
  return (
    <Stack gap="md" pb="xl">
      <OverviewHeader />

      <CriticalAttention />

      {/* What is happening, beside what is healthy. */}
      <div className="lh-overview-grid lh-overview-split">
        <LiveOperations />
        <SystemHealthPanel />
      </div>

      <ThroughputPanels />

      {/* Three again. Worker Utilization was briefly removed as worker-telemetry
          fiction, on the premise that no worker identity, heartbeat or sampling
          existed anywhere in the backend. That premise was wrong: HangFire.Server
          has always carried the server id, a refreshed heartbeat, the configured
          slot count and the queue list. What was missing was a way for the API to
          READ them — it holds no Hangfire reference (D14) — so the fix was a
          projection table, not a new subsystem. The panel is live now. */}
      <div className="lh-overview-grid lh-overview-thirds">
        <TopEntitiesChart />
        <RecentExecutions />
        <WorkerUtilization />
      </div>

      {/* Tables need width. The mockup put these three side by side, but a
          seven-column registry at a third of the page is unreadable. */}
      <div className="lh-overview-grid lh-overview-split">
        <SignalInboxPreview />
        <GovernanceQueuePreview />
      </div>

      <ReportRegistryPreview />
    </Stack>
  )
}
